import React, { useMemo } from 'react'
import Image from 'next/image'
import { SocialShareButtons } from '../'
import { CompareCredit } from '../../../types/compare-credit'
import isEmpty from 'lodash/fp/isEmpty'
import propOr from 'lodash/fp/propOr'
import Link from 'next/link'

import {
  getCurrentMonth,
  getCurrentYear,
  nextImageHelper,
  supify,
} from '../../utils'
import { useStatsigClient } from '@statsig/react-bindings'

export const TipHeaderDevice = (data: {
  cardOrderIds: Record<string, string>
  referencedCards: null | Record<string, CompareCredit.Entities>
  categoryId: string
  externalId: string
}) => {
  const TipHeaderDeviceRender = (props: {
    value: {
      author: CompareCredit.TipV2['author']
      reviewer?: CompareCredit.TipV2['author'][0]
      updatedDate: Date
      author_bar: {
        authorPicture: boolean
        hideAuthorBio: boolean
        dateVariation: boolean
        timeToRead: string
        reviewedBy: boolean
        showSocialIcons: boolean
      }
      borderBottom: boolean
      headline: string
      showAuthor: true
      subtitleEditorToggle: boolean
      subtitle: string
      subtitleAdvertorial: any
      underline: boolean
      location: string
      displayEditorialNote: boolean
      cardTips: CompareCredit.CardTip[]
    }
  }) => {
    const { getDynamicConfig } = useStatsigClient()
    const { referencedCards } = data

    // 1. CARDS
    const cardSlugs = props.value.cardTips.map((cardTip) => cardTip.card.slug)
    const cards = isEmpty(referencedCards)
      ? []
      : (cardSlugs
          .map((slug) => propOr(null, slug, referencedCards))
          .filter((x) => x) as CompareCredit.FormattedCard[])

    const {
      author,
      reviewer,
      updatedDate,
      author_bar,
      headline,
      showAuthor,
      location,
    } = props.value

    const showReviewedBy =
      author_bar.reviewedBy &&
      reviewer &&
      reviewer.slug &&
      reviewer.authorBioToggle

    const lastUpdatedDate = useMemo(() => {
      let lastUpdatedDate = updatedDate
      const cards = Object.values(referencedCards || {})
      if (cards && cards.length) {
        cards.forEach((card) => {
          if (
            card._type === 'card' &&
            card._updatedAt &&
            new Date(card._updatedAt) > lastUpdatedDate
          ) {
            lastUpdatedDate = new Date(card._updatedAt)
          }
        })
      }
      return lastUpdatedDate
    }, [updatedDate, referencedCards])

    const socialIconsHide = true

    const tipsUtmTitlesConfig = getDynamicConfig('tips_utm_titles')
    const customPageTitle = tipsUtmTitlesConfig.get(
      'page_title',
      null,
    ) as string

    return (
      <div className="c-header-device__container">
        <div
          className="c-header-device__main / w-full-vw / relative / -mt-9 / pb-[3rem] md:pb-[3.5rem] lg:pb-16 / pt-10 / 
        bg-gradient-to-t from-[#E7F9FF] to-white / overflow-hidden"
        >
          <div className="c-header-device__wrap / container-fluid / flex items-center / min-h-[8rem] xs:min-h-[10rem] md:min-h-[8rem]">
            <div className="c-header-device__header relative / w-full max-w-4xl / mx-auto">
              <div className="c-header-device__content / w-[65%] sm:w-[70%] lg:w-[75%]">
                <h1
                  className="c-header-device__h1 / u-sup / mb-3 / font-serif text-primary font-bold leading-[1.1] / 
                      text-[24px] xs:text-[32px] sm:text-[34px] md:text-[38px] lg:text-[42px]"
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: supify(customPageTitle ?? headline),
                    }}
                  />
                </h1>

                {showAuthor && author && author.length > 0 ? (
                  <div className="c-author / flex items-start / mb-2">
                    <div
                      className={`c-author__container / flex items-center / w-full / mb-1 ${
                        !author_bar.hideAuthorBio ? 'sm:items-start' : ''
                      }`}
                    >
                      {author_bar.authorPicture && (
                        <span className="c-author__pic / w-10 h-10 xs:w-12 xs:h-12 / inline-block shrink-0 / bg-gray-300 rounded-full / overflow-hidden / mr-2" />
                      )}
                      <div className="c-author__content w-full">
                        <div
                          className={`flex flex-col sm:flex-row sm:justify-between sm:items-end / w-full ${
                            showReviewedBy ? '' : 'sm:items-end'
                          }`}
                        >
                          <div className="flex flex-col / text-fs13 text-gray-700 leading-tight">
                            <p className="c-author__byline font-bold / sm:mb-1">
                              By {author[0].name}
                              {lastUpdatedDate && !author_bar.dateVariation && (
                                <span className="inline-block w-full sm:w-auto / sm:ml-2 / font-normal italic text-gray-600">
                                  Updated on {getCurrentMonth(lastUpdatedDate)}{' '}
                                  {lastUpdatedDate.getDate()},{' '}
                                  {getCurrentYear(lastUpdatedDate)}
                                </span>
                              )}
                            </p>

                            {!author_bar.hideAuthorBio && (
                              <p className="hidden sm:flex">
                                {author[0].description}
                              </p>
                            )}
                            {author_bar.dateVariation && (
                              <p className="c-author__date / inline-block w-full sm:w-auto font-normal text-gray-600">
                                Updated on {getCurrentMonth(lastUpdatedDate)}{' '}
                                {lastUpdatedDate.getDate()},{' '}
                                {getCurrentYear(lastUpdatedDate)}
                                {author_bar.timeToRead && (
                                  <span className="inline-block ml-1">
                                    &#8226; {author_bar.timeToRead}
                                  </span>
                                )}
                              </p>
                            )}
                          </div>

                          <div
                            className={`flex flex-col shrink-0 items-start sm:items-end / sm:ml-4 ${
                              showReviewedBy
                                ? 'xs:flex-row sm:flex-col sm:-mt-3'
                                : ''
                            }`}
                          >
                            {showReviewedBy && (
                              <p className="sm:order-2 flex items-center w-full sm:w-auto / mb-1 sm:mb-0 xs:mt-1 / text-fs13 text-gray-600">
                                <span className="inline-block w-4 h-4 / mr-1">
                                  <Image
                                    src="/static/icons/icon-checkmark-secondary.svg"
                                    alt="checkmark"
                                    width={24}
                                    height={24}
                                    aria-hidden={true}
                                    style={nextImageHelper.responsive}
                                    sizes="100vw"
                                  />
                                </span>{' '}
                                Reviewed by{' '}
                                <Link
                                  href={
                                    reviewer?.slug
                                      ? `/author/${reviewer.slug}`
                                      : '#'
                                  }
                                  className="inline-block ml-1 text-primary-mid font-bold / hover:text-primary-bright"
                                >
                                  {reviewer?.name}
                                </Link>
                              </p>
                            )}
                            {socialIconsHide == true ? (
                              ''
                            ) : (
                              <>
                                {author_bar.showSocialIcons && (
                                  <div className="c-social-share / sm:order-1 flex flex-row justify-end items-end / sm:-mt-1">
                                    <SocialShareButtons
                                      headline={headline}
                                      location={location}
                                      seoTitle={headline || undefined}
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  lastUpdatedDate && (
                    <div className="c-author__date-wrap">
                      <span className="c-author__date / flex items-start sm:items-center / -mt-2 sm:mb-2 / w-full sm:w-auto / italic / sm:flex-row sm:justify-between / flex-col / text-fs13 text-gray-600 / font-bold">
                        Updated on {getCurrentMonth(lastUpdatedDate)}{' '}
                        {lastUpdatedDate.getDate()},{' '}
                        {getCurrentYear(lastUpdatedDate)}
                      </span>
                    </div>
                  )
                )}
              </div>

              <div
                className="c-device / top-[-4.5rem] xs:top-[-3.5rem] sm:top-[-3.25rem] md:top-[-2.5rem] lg:top-[-1rem] right-[-2.5rem] sm:right-[0] md:right-[1.5rem] / w-48 / 
                    scale-[.65] xs:scale-[.75] sm:scale-[.8] lg:scale-[.9] / rotate-[5deg] lg:rotate-[0deg] / lg:skew-x-[-4deg] / skew-y-[-2deg] lg:skew-y-[4deg]"
              >
                <div
                  className="c-device__inner / absolute top-[10%] left-[-7%] right-0 / w-7/12 h-[70%] / mx-auto / 
                      skew-x-[-6deg] skew-y-[3deg]
                      "
                >
                  {cards.length > 0 ? (
                    cards.map((card, index) => (
                      <div
                        className={`c-device__card c-device__card-${
                          index + 1
                        } / rounded overflow-hidden`}
                        key={card.slug}
                      >
                        <img
                          src={card.cardArt.url}
                          alt="phone"
                          aria-hidden={true}
                        />
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="c-device__card c-device__card-1 / c-content-placeholder-bg w-full h-16 rounded-sm bg-gray-200">
                        &nbsp;
                      </div>
                      <div className="c-device__card c-device__card-2 / c-content-placeholder-bg w-full h-16 rounded-sm bg-gray-200">
                        &nbsp;
                      </div>
                      <div className="c-device__card c-device__card-3 / c-content-placeholder-bg w-full h-16 rounded-sm bg-gray-200">
                        &nbsp;
                      </div>
                    </>
                  )}
                </div>
                <div className="c-device__phone / w-full">
                  <img
                    src="/static/images/device-phone-slant.svg"
                    alt="phone"
                    aria-hidden={true}
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            className="c-header-device__bot / absolute z-2 / bg-no-repeat / w-full overflow-hidden"
            role="presentation"
          >
            <img
              src="/static/images/bg-bot-mask-white-sm.png"
              className="c-header-device__bg-mask scale-x-[-100%]"
              alt="mask"
              aria-hidden={true}
            />
          </div>
        </div>
        <style jsx>{`
          .c-author__pic {
            background-image: url(${author[0].image.url});
            background-size: 125% auto;
            background-position: center top;
            background-repeat: none;
          }
          .c-header-device__main {
            background-image: url(/static/images/bg-mesh-blue-light.jpg);
            background-size: cover;
            background-position: bottom center;
            background-repeat: no-repeat;
          }
          .c-device {
            position: absolute;
          }
          .c-device__card {
            position: absolute;
            top: 0;
            box-shadow: 4px 4px 2px 0 rgba(0, 0, 0, 0.2);
            animation-timing-function: ease;
            animation-duration: 8s;
            animation-fill-mode: forwards;
            animation-iteration-count: infinite;
          }
          .c-device__card-1 {
            animation-name: animateCard1;
          }
          .c-device__card-2 {
            top: 35%;
            animation-name: animateCard2;
          }
          .c-device__card-3 {
            top: 70%;
            animation-name: animateCard3;
          }

          @keyframes animateCard1 {
            0% {
              top: 0;
              filter: blur(1px);
              z-index: 1;
            }
            3% {
              top: 0;
              z-index: 1;
            }
            33% {
              top: 70%;
              z-index: 1;
            }
            36% {
              top: 70%;
              transform: scale(1) translateX(0);
              filter: blur(1px);
              z-index: 4;
              box-shadow: 4px 4px 2px 0 rgba(0, 0, 0, 0.2);
            }
            66% {
              top: 35%;
              transform: scale(1.2) translateX(-2rem);
              filter: blur(0);
              z-index: 4;
              box-shadow: 5px 7px 3px 0 rgba(0, 0, 0, 0.15);
            }
            69% {
              top: 35%;
              transform: scale(1.2) translateX(-2rem);
              filter: blur(0);
              z-index: 4;
              box-shadow: 5px 7px 3px 0 rgba(0, 0, 0, 0.15);
            }
            100% {
              top: 0;
              transform: scale(1) translateX(0);
              filter: blur(1px);
              z-index: 2;
              box-shadow: 4px 4px 2px 0 rgba(0, 0, 0, 0.2);
            }
          }

          @keyframes animateCard2 {
            0% {
              top: 35%;
              transform: scale(1.2) translateX(-2rem);

              filter: blur(0);
              z-index: 4;
              box-shadow: 5px 7px 3px 0 rgba(0, 0, 0, 0.15);
            }
            3% {
              top: 35%;
              transform: scale(1.2) translateX(-2rem);
              filter: blur(0);
              z-index: 4;
              box-shadow: 5px 7px 3px 0 rgba(0, 0, 0, 0.15);
            }
            33% {
              top: 0%;
              transform: scale(1) translateX(0);
              filter: blur(1px);
              z-index: 2;
              box-shadow: 4px 4px 2px 0 rgba(0, 0, 0, 0.2);
            }
            36% {
              top: 0%;
              transform: scale(1);
              filter: blur(1px);
              z-index: 2;
            }
            66% {
              top: 70%;
              transform: scale(1);
              filter: blur(1px);
              z-index: 1;
            }
            69% {
              top: 70%;
              transform: scale(1) translateX(0);
              filter: blur(1px);
              z-index: 1;
              box-shadow: 4px 4px 2px 0 rgba(0, 0, 0, 0.2);
            }
            100% {
              top: 35%;
              transform: scale(1.2) translateX(-2rem);
              filter: blur(0);
              z-index: 4;
              box-shadow: 5px 7px 3px 0 rgba(0, 0, 0, 0.15);
            }
          }

          @keyframes animateCard3 {
            0% {
              top: 70%;
              transform: scale(1) translateX(0);
              filter: blur(1px);
              z-index: 1;
            }
            3% {
              top: 70%;
              transform: scale(1) translateX(0);
              filter: blur(1px);
              z-index: 1;
              box-shadow: 4px 4px 2px 0 rgba(0, 0, 0, 0.2);
            }
            33% {
              top: 35%;
              transform: scale(1.2) translateX(-2rem);
              filter: blur(0);
              z-index: 4;
              box-shadow: 5px 7px 3px 0 rgba(0, 0, 0, 0.15);
            }
            36% {
              top: 35%;
              transform: scale(1.2) translateX(-2rem);
              filter: blur(0);
              z-index: 4;
              box-shadow: 5px 7px 3px 0 rgba(0, 0, 0, 0.15);
            }
            66% {
              top: 0;
              transform: scale(1) translateX(0);

              filter: blur(1px);
              z-index: 2;
              box-shadow: 4px 4px 2px 0 rgba(0, 0, 0, 0.2);
            }
            69% {
              top: 0;
              transform: scale(1);
              filter: blur(1px);
              z-index: 1;
            }
            100% {
              top: 70%;
              transform: scale(1);
              filter: blur(1px);
              z-index: 1;
            }
          }

          /*===== MASK BOTTOM =====*/
          .c-header-device__bot {
            bottom: -1px;
          }
          .c-header-device__bg-mask {
            width: 100%;
            height: 2rem;
            margin-bottom: -1px;
          }
          @media (min-width: 768px) {
            .c-header-device__bg-mask {
              height: 3rem;
            }
          }
        `}</style>
      </div>
    )
  }
  return TipHeaderDeviceRender
}
